import React, { useState, useRef, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { cn } from '../../utils';

interface TimeInputProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    error?: string;
    disabled?: boolean;
    className?: string;
    selectClassName?: string;
    defaultScrollTo?: string;
    required?: boolean;
}

const SLOTS: string[] = [];
for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
        SLOTS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
}

const to12Hour = (slot: string): string => {
    const [hStr, mStr] = slot.split(':');
    let h = Number(hStr);
    const suffix = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return `${h}:${mStr} ${suffix}`;
};

const TimeInput: React.FC<TimeInputProps> = ({
    value,
    onChange,
    label,
    error,
    disabled,
    className,
    defaultScrollTo,
    required,
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (open && scrollRef.current) {
            const targetSlot = value || defaultScrollTo;
            if (targetSlot) {
                const index = SLOTS.indexOf(targetSlot);
                if (index >= 0) {
                    const row = Math.floor(index / 4);
                    const rowHeight = 72;
                    scrollRef.current.scrollTop = Math.max(0, row * rowHeight - rowHeight);
                }
            }
        }
    }, [open, value, defaultScrollTo]);

    const displayValue = value ? to12Hour(value) : 'Seleccionar hora';

    return (
        <div ref={containerRef} className={cn('w-full', className)}>
            {label && (
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                    {label}{required && <span className="text-red-500 ml-0.5">*</span>}
                </label>
            )}

            <button
                type="button"
                disabled={disabled}
                onClick={() => setOpen((prev) => !prev)}
                className={cn(
                    'flex h-11 w-full items-center justify-between rounded-lg border bg-white px-4 text-sm transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-retri-blue focus-visible:ring-offset-2',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    error ? 'border-red-500 border-2' : 'border-2 border-gray-400 shadow-sm',
                    value ? 'text-gray-900' : 'text-gray-400',
                )}
            >
                <span>{displayValue}</span>
                <Clock className="h-4 w-4 text-gray-400" />
            </button>

            {open && !disabled && (
                <div ref={scrollRef} className="mt-2 max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-gray-50 p-2 shadow-lg">
                    <div className="grid grid-cols-4 gap-1.5">
                        {SLOTS.map((slot) => {
                            const isSelected = slot === value;
                            return (
                                <button
                                    key={slot}
                                    type="button"
                                    onClick={() => {
                                        onChange(slot);
                                        setOpen(false);
                                    }}
                                    style={{ height: 'calc(.25rem * 18)' }}
                                    className={cn(
                                        'px-2 py-2 rounded-lg text-sm font-medium transition-all relative flex items-center justify-center',
                                        isSelected
                                            ? 'bg-gray-900 text-white ring-2 ring-gray-900 ring-offset-1'
                                            : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100',
                                    )}
                                >
                                    {to12Hour(slot)}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
};

export default TimeInput;
